import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IpNamedLocation } from '@microsoft/microsoft-graph-types-beta';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SnackbarService } from 'projects/angular-clarity/src/app/services/snackbar.service';
import {
    createNamedLocation,
    createNamedLocationFailure,
    createNamedLocationSuccess,
    deleteNamedLocation,
    deleteNamedLocationFailure,
    deleteNamedLocationSuccess,
    updateNamedLocation,
    updateNamedLocationFailure,
    updateNamedLocationSuccess,
} from 'projects/angular-clarity/src/app/stores/client/graph/named-locations/actions';
import { ipCidrValidator } from 'projects/angular-clarity/src/app/utils/custom-validators';

@Component({
    selector: 'app-create-ip-named-location',
    templateUrl: './create-ip-named-location.component.html',
    styleUrls: ['./create-ip-named-location.component.scss'],
})
export class CreateIpNamedLocationComponent implements OnInit {
    form: FormGroup;
    isCreating = false;
    showInput: boolean;
    @Input() tenantId;
    @Input() itemToBeUpdated: IpNamedLocation;
    @Output() close = new EventEmitter<void>();

    ipRanges = [];

    constructor(
        private store: Store<any>,
        private fb: FormBuilder,
        private actions$: Actions,
        private snackbar: SnackbarService,
    ) {}

    ngOnInit(): void {
        if (this.itemToBeUpdated) {
            this.ipRanges = this.itemToBeUpdated.ipRanges.map((ip) => ip['cidrAddress']);
        }
        this.form = this.initForm();
    }

    initForm(): FormGroup {
        return this.fb.group({
            displayName: [this.itemToBeUpdated?.displayName, Validators.required],
            cidrAddress: ['', [ipCidrValidator()]],
            isTrusted: [this.itemToBeUpdated?.isTrusted || false],
        });
    }

    save() {
        this.isCreating = true;
        const ipRanges = this.ipRanges.map((ip) => {
            return {
                '@odata.type': this.detectIpType(ip),
                cidrAddress: ip,
            };
        });
        const location = {
            '@odata.type': '#microsoft.graph.ipNamedLocation',
            displayName: this.form.value.displayName,
            ipRanges,
            isTrusted: this.form.value.isTrusted || false,
        };

        if (this.itemToBeUpdated?.id) {
            this.update(location);
        } else {
            this.create(location);
        }
    }

    create(location: IpNamedLocation) {
        this.store.dispatch(createNamedLocation({ _tenant: this.tenantId, location }));

        this.actions$.pipe(ofType(createNamedLocationSuccess)).subscribe((_) => {
            this.isCreating = false;
            this.snackbar.success('IP named location has been added successfully');
            this.close.emit();
        });

        this.actions$.pipe(ofType(createNamedLocationFailure)).subscribe((error) => {
            this.isCreating = false;
            console.log(error);
            this.snackbar.error(error.error.message);
        });
    }
    update(location: IpNamedLocation) {
        location.id = this.itemToBeUpdated.id;
        this.store.dispatch(updateNamedLocation({ _tenant: this.tenantId, location }));

        this.actions$.pipe(ofType(updateNamedLocationSuccess)).subscribe((_) => {
            this.isCreating = false;
            this.snackbar.success('Ip named location has been updated successfully');
            this.close.emit();
        });

        this.actions$.pipe(ofType(updateNamedLocationFailure)).subscribe((error) => {
            this.isCreating = false;
            console.log(error);
            this.snackbar.error(error.error.message);
        });
    }

    delete() {
        this.store.dispatch(deleteNamedLocation({ _tenant: this.tenantId, id: this.itemToBeUpdated.id }));

        this.actions$.pipe(ofType(deleteNamedLocationSuccess)).subscribe((_) => {
            this.snackbar.success('Ip named location has been deleted successfully');
            this.close.emit();
        });

        this.actions$.pipe(ofType(deleteNamedLocationFailure)).subscribe((error) => {
            this.snackbar.error(error.error.message);
        });
    }

    detectIpType(cidr: string): '#microsoft.graph.iPv4CidrRange' | '#microsoft.graph.iPv6CidrRange' {
        const ipv4CidrRegex = /^(([0-9]{1,3}\.){3}[0-9]{1,3})\/([0-9]|[1-2][0-9]|3[0-2])$/;
        // const ipv6CidrRegex = /^([0-9a-fA-F:]{2,39})\/([0-9]|[1-9][0-9]|1[01][0-9]|12[0-8])$/;

        if (ipv4CidrRegex.test(cidr)) return '#microsoft.graph.iPv4CidrRange';
        // if (ipv6CidrRegex.test(cidr)) return '#microsoft.graph.iPv6CidrRange';
        return '#microsoft.graph.iPv6CidrRange';
    }

    addIp() {
        this.ipRanges.push(this.form.value.cidrAddress.trim());
        this.form.get('cidrAddress').reset();
        this.showInput = false;
    }

    onCancel() {
        this.close.emit();
    }
    onCreate() {
        this.close.emit(undefined);
    }
}
